import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { RewardChip } from '@/features/guide-rewards/components/RewardChip';
import { adminGrowthApi, adminGrowthKeys, type AdminPhoto } from '../api/admin-growth.api';
import { FilterBar, ReasonDialog, fmtDateTime, humanize, useGrowthMutation } from './GrowthShared';

function CheckIcon({ passed }: { passed: boolean | null }) {
  if (passed === true) return <CheckCircleIcon fontSize="inherit" color="success" />;
  if (passed === false) return <CancelIcon fontSize="inherit" color="error" />;
  return <HelpOutlineIcon fontSize="inherit" color="warning" />;
}

export function GrowthPhotosPanel() {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState('REVIEW_REQUIRED');
  const [type, setType] = useState('');
  const [viewing, setViewing] = useState<AdminPhoto | null>(null);
  const [action, setAction] = useState<{ kind: 'approve' | 'reject'; photo: AdminPhoto } | null>(null);

  const query = { page: page + 1, limit, status: status || undefined, type: type || undefined };
  const { data, isLoading, isError } = useQuery({
    queryKey: adminGrowthKeys.photos(query),
    queryFn: () => adminGrowthApi.photos(query),
    placeholderData: keepPreviousData,
  });

  const approve = useGrowthMutation(({ id, note }: { id: string; note?: string }) => adminGrowthApi.approvePhoto(id, note), 'Photo approved');
  const reject = useGrowthMutation(({ id, reason }: { id: string; reason: string }) => adminGrowthApi.rejectPhoto(id, reason), 'Photo rejected — the guide was told why');
  const retry = useGrowthMutation((id: string) => adminGrowthApi.retryPhoto(id), 'Re-validation queued');

  return (
    <>
      <FilterBar>
        <TextField size="small" select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {['REVIEW_REQUIRED', 'PENDING', 'PROCESSING', 'APPROVED', 'REJECTED'].map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Type" value={type} onChange={(e) => { setType(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="T_SHIRT_SELFIE">T-shirt selfie</MenuItem>
          <MenuItem value="TRAVELLER_PHOTO">Traveller photo</MenuItem>
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
          Automatic checks cover file validity, quality, duplicates and eligibility. No vision provider is configured, so content (person, shirt, Trplin logo) is confirmed by you here.
        </Typography>
      </FilterBar>

      {isError ? (
        <Typography color="error">Could not load photos.</Typography>
      ) : isLoading ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={340} />)}
        </Box>
      ) : !data?.items.length ? (
        <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>No photos match these filters.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {data.items.map((photo) => (
            <Paper key={photo.id} variant="outlined" sx={{ borderRadius: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Box component="button" onClick={() => setViewing(photo)} sx={{ all: 'unset', cursor: 'zoom-in', display: 'block' }} aria-label="View full photo">
                <Box component="img" src={photo.url} alt="" loading="lazy" sx={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', bgcolor: 'grey.100' }} />
              </Box>
              <Box sx={{ p: 1.5, display: 'grid', gap: 1, flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography fontWeight={700} fontSize="0.875rem">{humanize(photo.type)}</Typography>
                  <RewardChip label={humanize(photo.status)} status={photo.status} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {[photo.guide?.firstName, photo.guide?.lastName].filter(Boolean).join(' ') || photo.guide?.email || 'Guide'} · {fmtDateTime(photo.createdAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {photo.width}×{photo.height}px · {(photo.bytes / 1024).toFixed(0)} KB
                  {photo.type === 'TRAVELLER_PHOTO' && ` · consent ${photo.consentConfirmedAt ? '✓' : '✗'}`}
                </Typography>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.25 }}>
                  {photo.checks.map((check) => (
                    <Box component="li" key={check.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.78rem' }} title={check.detail}>
                      <CheckIcon passed={check.passed} /> {humanize(check.name)}
                    </Box>
                  ))}
                </Box>
                {photo.rejectionReason && <Typography variant="caption" color="error.main">{photo.rejectionReason}</Typography>}
                <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 1, flexWrap: 'wrap' }}>
                  {['REVIEW_REQUIRED', 'REJECTED'].includes(photo.status) && (
                    <Button size="small" variant="contained" color="success" onClick={() => setAction({ kind: 'approve', photo })}>Approve</Button>
                  )}
                  {photo.status !== 'APPROVED' && photo.status !== 'REJECTED' && (
                    <Button size="small" color="error" onClick={() => setAction({ kind: 'reject', photo })}>Reject</Button>
                  )}
                  {['REJECTED', 'REVIEW_REQUIRED', 'PROCESSING'].includes(photo.status) && (
                    <Button size="small" onClick={() => retry.mutate(photo.id)} disabled={retry.isPending}>Re-run checks</Button>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
      {Boolean(data?.total) && (
        <TablePagination
          component="div"
          count={data?.total ?? 0}
          page={page}
          rowsPerPage={limit}
          onPageChange={(_e, next) => setPage(next)}
          onRowsPerPageChange={(e) => { setLimit(Number(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100]}
        />
      )}

      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="lg">
        <IconButton onClick={() => setViewing(null)} aria-label="Close" sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(255,255,255,0.85)' }}><CloseIcon /></IconButton>
        {viewing && <Box component="img" src={viewing.fullUrl} alt="" sx={{ display: 'block', maxWidth: '100%', maxHeight: '85vh' }} />}
      </Dialog>

      <ReasonDialog
        open={Boolean(action)}
        pending={approve.isPending || reject.isPending}
        required={action?.kind === 'reject'}
        danger={action?.kind === 'reject'}
        title={action?.kind === 'approve' ? 'Approve this photo?' : 'Reject this photo?'}
        description={
          action?.kind === 'approve'
            ? action.photo.type === 'T_SHIRT_SELFIE'
              ? 'Confirm a person is wearing the official Trplin T-shirt with the logo visible. The selfie reward is credited automatically.'
              : 'Confirm this is a genuine photo from the trip. It counts toward the guide’s photo milestone.'
            : 'The guide sees this reason and can upload a different photo.'
        }
        confirmLabel={action?.kind === 'approve' ? 'Approve' : 'Reject'}
        onClose={() => setAction(null)}
        onConfirm={(reason) => {
          if (!action) return;
          const done = { onSuccess: () => setAction(null) };
          if (action.kind === 'approve') approve.mutate({ id: action.photo.id, note: reason || undefined }, done);
          else reject.mutate({ id: action.photo.id, reason }, done);
        }}
      />
    </>
  );
}
